import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { ConnectedPeopleResponse, UserSummaryResponse } from '@lifetimeline/shared';

import { AutoLoad } from '../components/AutoLoad.js';
import { BackLink } from '../components/BackLink.js';
import { EmptyState } from '../components/EmptyState.js';
import { Layout } from '../components/Layout.js';
import { Notice } from '../components/Notice.js';
import { PersonList, PersonRow } from '../components/PersonList.js';
import { Spinner } from '../components/Spinner.js';
import { useToast } from '../components/Toast.js';
import { useI18n } from '../i18n/index.js';
import { api, errorMessageKey } from '../lib/apiClient.js';
import styles from './pageStyles.module.css';

/**
 * 남의 인연 — `/@junho`의 인연 수를 누르면 온다.
 *
 * **이어진 사람만** 나온다. 오간 신청은 당사자들의 일이라 내 인연 화면
 * (`ConnectionsPage`)에만 있다 — 그래서 여기는 한 덩어리뿐이고 줄에 버튼도
 * 없다. 누를 것은 사람 그 자체다.
 *
 * 잠긴 계정은 서버가 `USER_NOT_FOUND`로 막는다. 화면이 가리는 게 아니라
 * 애초에 내려오지 않는다.
 */
export function PublicConnectionsPage() {
  const { t } = useI18n();
  const toast = useToast();
  const params = useParams();
  // 주소의 `@`는 라우팅 표기일 뿐 식별자가 아니다 — 떼고 쓴다
  const handle = (params.handle ?? '').replace(/^@/, '');

  const [people, setPeople] = useState<UserSummaryResponse[] | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const path = `/api/profiles/${encodeURIComponent(handle)}/connections`;

  const load = useCallback(async () => {
    try {
      const page = await api.get<ConnectedPeopleResponse>(path);
      setPeople(page.people);
      setCount(page.count);
      setCursor(page.nextCursor);
      setError(null);
    } catch (cause) {
      setError(t(errorMessageKey(cause)));
    }
  }, [path, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 다음 쪽을 이어 붙인다 — 스크롤이 닿으면 저절로 */
  const loadMore = async () => {
    if (cursor === null || more) {
      return;
    }
    setMore(true);
    try {
      const next = await api.get<ConnectedPeopleResponse>(
        `${path}?cursor=${encodeURIComponent(cursor)}`,
      );
      setPeople((current) => [...(current ?? []), ...next.people]);
      setCursor(next.nextCursor);
    } catch (cause) {
      toast.show(t(errorMessageKey(cause)), 'error');
    } finally {
      setMore(false);
    }
  };

  return (
    <Layout>
      <div className={styles.head}>
        {/* 돌아갈 곳은 내 프로필이 아니라 **보고 있던 사람**이다 */}
        <BackLink to={`/@${handle}`} />
        {/*
          제목은 **소제목 한 줄**이다 — 내 인연 화면의 구획 제목과 같은 모양.
          큰 글씨로 "인연"을 한 번 더 쓰면, 누르고 들어온 사람에게 이미 아는
          말을 제일 크게 보여주는 꼴이 된다. 대신 **몇 명인지**를 적는다.

          한 쪽만 읽어 왔어도 수는 세어 둔 값이라 정확하다.
        */}
        <h1 className={styles.sectionTitle}>
          {count === null ? '' : t('connection.count', { count })}
        </h1>
      </div>

      {error !== null ? (
        <div className={styles.noticeSlot}>
          <Notice tone="error">{error}</Notice>
        </div>
      ) : people === null ? (
        <div className={styles.centered}>
          <Spinner label={t('common.loading')} />
        </div>
      ) : people.length === 0 ? (
        <EmptyState label={t('connection.noneOther')} />
      ) : (
        <>
          <PersonList>
            {people.map((person) => (
              <PersonRow key={person.handle} person={person} />
            ))}
          </PersonList>
          {cursor === null ? null : <AutoLoad onLoad={() => void loadMore()} loading={more} />}
        </>
      )}
    </Layout>
  );
}
